import { api } from "./api.ts";
import { localeStore, t } from "./i18n.ts";
import { createStore } from "./store.ts";
import type { AttentionRow } from "../../shared/types.ts";

/**
 * The attention badge on the Dashboard nav link (round 160) - what turns the strip from a REPORT
 * into a NOTIFICATION.
 *
 * Round 120 put the app's broken promises on the dashboard with an argument that had teeth: "a
 * console line on a laptop is not an answer, and neither is a tab inside the release they are not
 * thinking about". A strip that only exists on one page of fifteen is a weaker version of the same
 * problem - the app knows a trial ends on Friday, that an armed autopilot stood down, that four
 * fans are stranded, and says all of it to whoever happens to navigate home. So the count follows
 * the owner around the app, and the one place it can point is the surface that already lists them.
 *
 * Three decisions worth keeping:
 *
 * - **It counts WARNINGS, never rows.** The severity is the server's (see `AttentionRow`), and it
 *   marks the rows that are a promise the app is not keeping rather than a chore waiting. A badge
 *   that also counted the approval queue would be lit every day of the week, and a badge that is
 *   always lit is one nobody reads - the same failure the operator console's "top row is MRR" note
 *   warns about, in a smaller space.
 * - **It refreshes on NAVIGATION, not on a timer.** There is no event behind a poll here; what
 *   changes these rows is the owner doing something, and every one of those ends in a navigation.
 *   A timer would spend a request a minute to be right a few seconds sooner about a fact whose
 *   units are days.
 * - **A failed read changes nothing.** The count stays whatever it last was rather than dropping to
 *   zero: `api()` fails on a dead server, an expired session and a lost network alike, and "your
 *   trial is fine now" is the one thing none of those mean. Same rule as the boot chrome in
 *   `app.ts` - never add or remove a surface on an error.
 */

/**
 * The rows behind the badge, published so a second reader costs no second request (2026-08-07).
 *
 * ⚠ **The point is that there is ONE fetch and ONE derivation.** `<gh-help-bubble>` needs to know how
 * many support answers are unread, and the obvious implementation - a `GET /api/support/tickets` of
 * its own, on every navigation - is a second source of truth for a number the strip already
 * computes. Two of those eventually disagree, and the day they do, the dot says something different
 * from the row it opens. So the bubble reads `dash.attn.supportAnswered`'s own `count` out of this.
 *
 * It is a store rather than an exported array because a subscriber has to KNOW when the value
 * changed - `store.ts`'s founding argument, and here the change arrives from a fetch nobody else
 * triggered.
 */
export const attentionStore = createStore<AttentionRow[]>([]);

/** Last count the server gave us, so a locale change can re-say it without a request */
let lastCount = 0;
let inFlight = false;

function badge(): HTMLElement | null {
	return document.getElementById("nav-attention");
}

function paint(count: number): void {
	const el = badge();
	if (el == null) return;
	lastCount = count;
	el.textContent = String(count);
	// The number alone is not a sentence - a screen reader (and a hover) get the whole one.
	el.title = t("nav.attentionBadge", { count });
	el.setAttribute("aria-label", t("nav.attentionBadge", { count }));
	el.classList.toggle("hidden", count === 0);
}

/**
 * Re-read the strip and repaint. Safe to call on every navigation: overlapping calls collapse to
 * one, and the route behind it is a handful of small queries rather than the dashboard's whole
 * payload (which is why it exists at all - see routes/attention.ts).
 */
export async function refreshAttentionBadge(): Promise<void> {
	if (inFlight) return;
	inFlight = true;
	try {
		const { rows } = await api<{ rows: AttentionRow[] }>("/api/attention");
		attentionStore.set(rows);
		paint(rows.filter(row => row.severity === "warning").length);
	} catch {
		// Deliberately nothing - see the header. The badge keeps saying what it last knew.
	} finally {
		inFlight = false;
	}
}

/** Wire the badge up: repaint on locale change, and count again after every navigation. */
export function initAttentionBadge(onNavigate: (listener: () => void) => void): void {
	localeStore.subscribe(() => paint(lastCount));
	onNavigate(() => void refreshAttentionBadge());
	void refreshAttentionBadge();
}
